// src/encounter/lib/medication-add-validation.ts
// Save-time validation for the rail's add/edit medication form. Ports Flutter's medications case of
// PatientSummaryData.validate (patient_summary_data.dart:80-159) with its exact messages, run per
// field so each error renders inline under its own control instead of one toast.
//
// One deliberate tightening (owner ruling): Flutter marks Start date mandatory but its else-if chain
// only reaches the date check when times-a-day is neither an int nor a String, so in practice it
// never fires. Here a missing start date always blocks a scheduled save.
import { MED_FREQUENCY, frequencyIsScheduled, type MedFrequency } from './medication-frequency';

export type MedicationAddField =
  | 'medication'
  | 'daysInterval'
  | 'specificDays'
  | 'timesADay'
  | 'unitsPerIntake'
  | 'startDate'
  | 'duration';

/** Visual order of the fields — the first error in this order takes focus. */
export const MEDICATION_ADD_FIELD_ORDER: readonly MedicationAddField[] = [
  'medication',
  'daysInterval',
  'specificDays',
  'timesADay',
  'unitsPerIntake',
  'startDate',
  'duration',
];

export const MEDICATION_ADD_MESSAGES: Record<MedicationAddField, string> = {
  medication: 'Invalid medication. Please select from dropdown.',
  daysInterval: 'Please enter days under days interval',
  specificDays: 'Select at least one day',
  timesADay: 'Times a day is zero',
  unitsPerIntake: 'Units per intake is zero',
  startDate: 'Please select start date of medicines',
  duration: 'Duration is zero',
};

export interface MedicationAddValues {
  hasMedication: boolean;
  /**
   * null = an EDITED record whose frequency the form cannot model (a legacy free-text "BID", or none
   * at all) and that the clinician has not re-picked. Its schedule is saved back unchanged, so only
   * the drug is checked — never a forced re-classification of old data just to edit its route.
   */
  frequency: MedFrequency | null;
  daysInterval: string;
  specificDays: readonly string[];
  timesADay: string;
  unitsPerIntake: string;
  startDate: string | undefined;
  duration: string;
}

/** A positive whole number ("0", "", "1.5" and "abc" all fail). */
function isPositiveInt(value: string): boolean {
  const v = value.trim();
  return /^\d+$/.test(v) && Number(v) > 0;
}

/** A positive decimal — units per intake may be a fraction (Flutter's `^\d*\.?\d*` formatter). */
function isPositiveDecimal(value: string): boolean {
  const v = value.trim();
  return /^\d*\.?\d+$|^\d+\.$/.test(v) && Number(v) > 0;
}

/** Every failing field with its Flutter message. Empty object = the form may save. */
export function validateMedicationAdd(v: MedicationAddValues): Partial<Record<MedicationAddField, string>> {
  const errors: Partial<Record<MedicationAddField, string>> = {};
  if (!v.hasMedication) errors.medication = MEDICATION_ADD_MESSAGES.medication;
  // As needed checks the drug only (Flutter: `if (frequency != frequencyNeeded) { … }`), and so
  // does an unchanged legacy schedule (see `frequency` above).
  if (!v.frequency || !frequencyIsScheduled(v.frequency)) return errors;
  if (v.frequency === MED_FREQUENCY.daysInterval && !isPositiveInt(v.daysInterval)) {
    errors.daysInterval = MEDICATION_ADD_MESSAGES.daysInterval;
  }
  if (v.frequency === MED_FREQUENCY.specificDays && v.specificDays.length === 0) {
    errors.specificDays = MEDICATION_ADD_MESSAGES.specificDays;
  }
  if (!isPositiveInt(v.timesADay)) errors.timesADay = MEDICATION_ADD_MESSAGES.timesADay;
  if (!isPositiveDecimal(v.unitsPerIntake)) errors.unitsPerIntake = MEDICATION_ADD_MESSAGES.unitsPerIntake;
  if (!v.startDate) errors.startDate = MEDICATION_ADD_MESSAGES.startDate;
  if (!isPositiveInt(v.duration)) errors.duration = MEDICATION_ADD_MESSAGES.duration;
  return errors;
}

/** The first failing field in visual order, or null. */
export function firstInvalidField(errors: Partial<Record<MedicationAddField, string>>): MedicationAddField | null {
  return MEDICATION_ADD_FIELD_ORDER.find((f) => errors[f]) ?? null;
}
